import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './Dashboard.css';
import { MultiSelectComponent } from '@syncfusion/ej2-react-dropdowns';
import '@syncfusion/ej2-base/styles/material.css';
import '@syncfusion/ej2-buttons/styles/material.css';
import '@syncfusion/ej2-inputs/styles/material.css';
import '@syncfusion/ej2-react-dropdowns/styles/material.css';
import { LANGUAGE_LIST, MILITARY_EXPERIENCE_LIST, EDUCATION_LEVEL_LIST, EXPERIENCE_LEVEL_LIST, JOB_CATEGORY_LIST, JOB_TYPE_LIST } from '../../constants/options';
import { useAuth } from '../../contexts/AuthContext';
import { useRoleMessages } from '../../contexts/RoleMessagesContext';
import { listResumes, setDefaultResume } from '../../services/resumes';
import { getSyncfusionMultiSelectA11yHandlers } from '../../utils/syncfusionMultiSelectA11y';
import { storeRefreshedTokens } from '../../utils/authTokens';

// Using centralized job categories for Primary Job Experience
// Using centralized EXPERIENCE_LEVEL_LIST for work levels
// Using centralized EDUCATION_LEVEL_LIST
// Use centralized JOB_TYPE_LIST for employment types
const VETERAN_STATUS = ['None', 'Veteran', 'Active Duty', 'Reservist', 'Military Spouse'];

// Required profile fields in DOM order. The order is used to determine which
// field receives focus first when validation fails (the first invalid field).
// `field` is the id of the focusable control; `errorId` is the unique id of the
// inline <span> that displays the error and is referenced via aria-describedby.
const REQUIRED_PROFILE_FIELDS = [
  { name: 'headline', field: 'headline', errorId: 'headline-error', label: 'Headline' },
  { name: 'keywords', field: 'keywords', errorId: 'keywords-error', label: 'Keywords' },
  { name: 'primaryExperience', field: 'primaryExperience', errorId: 'primaryExperience-error', label: 'Primary Experience' },
  { name: 'workLevel', field: 'workLevel', errorId: 'workLevel-error', label: 'Work Level' },
  { name: 'educationLevel', field: 'educationLevel', errorId: 'educationLevel-error', label: 'Education Level' },
  { name: 'employmentTypes', field: 'employmentTypes', errorId: 'employmentTypes-error', label: 'Employment Types' },
  { name: 'languages', field: 'languages', errorId: 'languages-error', label: 'Languages' },
];

// Syncfusion MultiSelect fields render a separate visible combobox; their ARIA
// (aria-invalid / aria-describedby) must be applied imperatively on that element.
const MULTISELECT_FIELDS = ['keywords', 'primaryExperience', 'employmentTypes', 'languages'];

const isEmptyValue = (value) =>
  value === undefined ||
  value === null ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'string' && value.trim() === '');

const parseKeywordTags = (keywords) => (keywords ? keywords.split(',').map(s => s.trim()).filter(Boolean) : []);

function getNameInitials(displayName) {
  if (!displayName || !String(displayName).trim()) return '?';
  const parts = String(displayName).trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

export default function EditProfileResume({
  onValidationChange,
  onFormDataChange,
  onDone,
  onPrev,
  embedded,
  resumeOptional,
  resumeTopSlot,
  resumeError
}) {
  // Standalone page uses h1 → h2; embedded wizard uses h3 → h4 (avoid skipped levels in either context)
  const ProfileSectionHeading = embedded ? 'h4' : 'h2';
  const CameraDialogHeading = embedded ? 'h4' : 'h2';
  const { user } = useAuth();
  const { getMessage } = useRoleMessages();
  const [form, setForm] = useState({
    headline: '',
    keywords: '',
    linkedInUrl: '',
    primaryExperience: [], // multi-select (max 2)
    workLevel: '',
    educationLevel: '',
    languages: [],
    employmentTypes: [], // multi-select
    veteranStatus: ''
  });
  const infoBannerMessage = getMessage('edit-profile', 'info-banner') || '';
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resumeFileName, setResumeFileName] = useState('');
  const [resumeUrl, setResumeUrl] = useState('');
  const [resumeKey, setResumeKey] = useState('');
  const [resumeStatusMessage, setResumeStatusMessage] = useState('');
  const [resumeTab, setResumeTab] = useState(() => localStorage.getItem('resumeTabPref') || 'upload');
  const [builderResumes, setBuilderResumes] = useState([]);
  const [builderLoading, setBuilderLoading] = useState(false);
  const [settingDefault, setSettingDefault] = useState(null);
  // Persistent live regions (refs) kept in the DOM at all times so VoiceOver and
  // other screen readers reliably announce upload/delete/added results. We update
  // textContent imperatively to avoid React batching collapsing the clear+set.
  const politeRegionRef = useRef(null);
  const assertiveRegionRef = useRef(null);
  const announceTimerRef = useRef(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState('');
  const [avatarKey, setAvatarKey] = useState('');
  const [pendingAvatarFile, setPendingAvatarFile] = useState(null);
  const [pendingAvatarPreview, setPendingAvatarPreview] = useState(null);
  const [pendingAvatarName, setPendingAvatarName] = useState('');
  const [pendingAvatarSource, setPendingAvatarSource] = useState('file'); // 'file' | 'camera'
  const [avatarImageError, setAvatarImageError] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const errorSummaryRef = useRef(null);
  const formRef = useRef(form);
  formRef.current = form;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const resumeInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const topRef = useRef(null);
  const successMessageRef = useRef(null);
  const cameraDialogRef = useRef(null);
  const cameraCaptureButtonRef = useRef(null);
  const cameraCancelButtonRef = useRef(null);
  const cameraTriggerRef = useRef(null);
  const returnFocusRef = useRef(null);

  const keywordTags = parseKeywordTags(form.keywords);
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  // Instruction for the profile picture section. The text reflects the current
  // state so sighted users see, and screen reader users hear (via the buttons'
  // aria-describedby reference), exactly what to do next.
  const getAvatarInstructions = () => {
    if (uploading) {
      return 'Uploading your photo. Please wait.';
    }
    if (pendingAvatarPreview) {
      return 'Your photo is ready. Review the preview below, then select the Confirm button to save it, or select Cancel to discard it.';
    }
    if (avatarPreviewUrl) {
      return 'Your profile picture is saved. To replace it, choose a new photo from your device or take a new picture, then select the Confirm button. You can also remove the current picture.';
    }
    return 'You can choose a photo from your device or take a photo with your camera. After you choose or take a photo, you must select the Confirm button to save it.';
  };
  const avatarInstructions = getAvatarInstructions();

  const announceToScreenReader = (message, type = 'success') => {
    const region = type === 'error' ? assertiveRegionRef.current : politeRegionRef.current;
    if (!region) return;
    // Clear first so an identical, consecutive message still re-announces, then
    // set the text on a short delay so the (already-present) live region fires.
    region.textContent = '';
    if (announceTimerRef.current) clearTimeout(announceTimerRef.current);
    announceTimerRef.current = setTimeout(() => {
      region.textContent = message;
    }, 100);
  };

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    announceToScreenReader(message, type);
    setTimeout(() => setToast({ visible: false, message: '', type }), 2500);
  };

  const deleteResume = async () => {
    try {
      if (!resumeKey) {
        showToast('No resume found to delete', 'error');
        setResumeStatusMessage('No resume file is currently attached.');
        return;
      }
      await authFetch(`/api/uploads/${encodeURIComponent(resumeKey)}`, { method: 'DELETE' });
      setResumeFileName('');
      setResumeKey('');
      setResumeUrl('');
      showToast('Resume deleted', 'success');
      setResumeStatusMessage('Resume deleted successfully.');
      if (resumeInputRef.current) resumeInputRef.current.value = '';
    } catch (e) {
      showToast('Failed to delete resume', 'error');
      setResumeStatusMessage('Resume delete failed. Please try again.');
    }
  };

  const deleteAvatar = async () => {
    try {
      if (avatarKey) {
        // Try to delete from S3 first if we have the key
        try {
          await authFetch(`/api/uploads/${encodeURIComponent(avatarKey)}`, { method: 'DELETE' });
        } catch (e) {
          // If S3 delete fails, fall back to just removing from user profile
          console.warn('S3 delete failed, removing from profile only:', e);
        }
      }
      // Always remove from user profile
      await authFetch('/api/users/me', {
        method: 'PUT',
        body: JSON.stringify({ avatarUrl: null })
      });
      setAvatarPreviewUrl('');
      setAvatarKey('');
      showToast('Profile picture removed', 'success');
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    } catch (e) {
      showToast('Failed to remove profile picture', 'error');
    }
  };

  const handleAvatarFileSelect = (file, source = 'file') => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }
    
    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      showToast('Image size must be less than 2MB', 'error');
      return;
    }
    
    // Create preview
    const previewUrl = URL.createObjectURL(file);
    setPendingAvatarFile(file);
    setPendingAvatarPreview(previewUrl);
    setPendingAvatarSource(source);
    setPendingAvatarName(source === 'camera' ? '' : (file.name || ''));
    const addedLabel = source === 'camera'
      ? 'Photo taken.'
      : `Photo selected: ${file.name || 'image file'}.`;
    announceToScreenReader(`${addedLabel} Preview shown. Select Confirm to save, or Cancel to discard.`);
  };

  const confirmAvatarUpload = async () => {
    if (!pendingAvatarFile) return;
    await uploadToS3(pendingAvatarFile, 'avatar');
    // Clean up pending state
    if (pendingAvatarPreview) {
      URL.revokeObjectURL(pendingAvatarPreview);
    }
    setPendingAvatarFile(null);
    setPendingAvatarPreview(null);
    setPendingAvatarName('');
    setPendingAvatarSource('file');
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  const cancelAvatarUpload = () => {
    if (pendingAvatarPreview) {
      URL.revokeObjectURL(pendingAvatarPreview);
    }
    setPendingAvatarFile(null);
    setPendingAvatarPreview(null);
    setPendingAvatarName('');
    setPendingAvatarSource('file');
    if (avatarInputRef.current) avatarInputRef.current.value = '';
    announceToScreenReader('Profile picture discarded.');
  };

  // Bind stream to video when modal is open and video is mounted
  useEffect(() => {
    const attach = async () => {
      if (!cameraOpen) return;
      const stream = mediaStreamRef.current;
      if (videoRef.current && stream) {
        try {
          videoRef.current.srcObject = stream;
          // Some browsers require canplay before play
          const playPromise = videoRef.current.play();
          if (playPromise && typeof playPromise.then === 'function') {
            await playPromise.catch(() => { });
          }
        } catch (_) { }
      }
    };
    attach();
    return () => {
      // no-op; stream is stopped on closeCamera
    };
  }, [cameraOpen]);

  // Always read latest token and provide an auth-fetch helper BEFORE any hooks use it
  const getToken = () => localStorage.getItem('token');
  const getRefreshToken = () => localStorage.getItem('refreshToken');

  const tryRefreshToken = async () => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) return false;
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
      if (!res.ok) return false;
      const data = await res.json();
      // Skips the write if the user logged out or switched accounts while this was in flight
      return storeRefreshedTokens(refreshToken, data?.tokens);
    } catch {
      return false;
    }
  };

  const authFetch = async (url, options = {}, retry = true) => {
    const token = getToken();
    const res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
        ...(options.headers || {})
      }
    });
    if (res.status === 401 && retry) {
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        return authFetch(url, options, false);
      }
    }
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Request failed: ${res.status}`);
    }
    return res.json();
  };

  // Notify parent of form data changes (including resume status). Wait until the
  // profile has loaded so a remount does not report an empty form and trigger
  // false validation errors in the registration wizard.
  useEffect(() => {
    if (!onFormDataChange || !profileReady) return;
    onFormDataChange({
      ...form,
      resumeUrl: resumeUrl,
      hasResume: !!resumeUrl || !!resumeFileName
    });
  }, [form, resumeUrl, resumeFileName, onFormDataChange, profileReady]);

  const loadBuilderResumes = useCallback(async () => {
    setBuilderLoading(true);
    try {
      const data = await listResumes();
      setBuilderResumes(data?.resumes || data || []);
    } catch { /* non-fatal */ } finally {
      setBuilderLoading(false);
    }
  }, []);

  useEffect(() => { loadBuilderResumes(); }, [loadBuilderResumes]);

  const handleSetDefault = async (id) => {
    setSettingDefault(id);
    try {
      await setDefaultResume(id);
      await loadBuilderResumes();
      setResumeTab('builder');
      localStorage.setItem('resumeTabPref', 'builder');
      showToast('Resume set as profile resume');
    } catch {
      showToast('Failed to set resume', 'error');
    } finally {
      setSettingDefault(null);
    }
  };

  // Prefill from backend on mount
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await authFetch('/api/users/me', { method: 'GET' });
        const prof = data?.profile || {};
        setForm(prev => ({
          ...prev,
          headline: prof.headline || '',
          keywords: prof.keywords || '',
          linkedInUrl: data?.user?.linkedInUrl || prof.linkedInUrl || '',
          primaryExperience: Array.isArray(prof.primaryExperience) ? prof.primaryExperience : [],
          workLevel: prof.workLevel || '',
          educationLevel: prof.educationLevel || '',
          languages: Array.isArray(prof.languages) ? prof.languages : [],
          employmentTypes: Array.isArray(prof.employmentTypes) ? prof.employmentTypes : [],
          veteranStatus: prof.veteranStatus || ''
        }));

        const avatarUrl = data?.user?.avatarUrl;
        if (avatarUrl) {
          setAvatarPreviewUrl(avatarUrl);
          try {
            const u = new URL(avatarUrl, window.location.origin);
            const pathname = u.pathname || '';
            if (pathname.startsWith('/api/uploads/public/')) {
              const key = decodeURIComponent(pathname.slice('/api/uploads/public/'.length));
              if (key.startsWith('avatar/')) setAvatarKey(key);
            } else {
              const path = decodeURIComponent(pathname.startsWith('/') ? pathname.slice(1) : pathname);
              if (path && path.startsWith('avatar/')) setAvatarKey(path);
            }
          } catch {
            try {
              const u = new URL(avatarUrl);
              const path = decodeURIComponent(u.pathname.startsWith('/') ? u.pathname.slice(1) : u.pathname);
              if (path && path.startsWith('avatar/')) setAvatarKey(path);
            } catch {
              /* ignore */
            }
          }
        }

        const rUrl = data?.user?.resumeUrl;
        if (rUrl) {
          try {
            const u = new URL(rUrl);
            const path = decodeURIComponent(u.pathname.startsWith('/') ? u.pathname.slice(1) : u.pathname);
            // path is the S3 key, e.g., "resume/<userId>/<uuid>.pdf"
            if (path) setResumeKey(path);
            const fname = path.split('/').pop();
            if (fname) setResumeFileName(fname);
            setResumeUrl(rUrl);
            if (fname) {
              setResumeStatusMessage(`Current attached resume: ${fname}.`);
            }
          } catch { }
        }
      } catch (e) {
        showToast('Failed to load profile', 'error');
      } finally {
        setProfileReady(true);
      }
    };
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      if (pendingAvatarPreview) {
        URL.revokeObjectURL(pendingAvatarPreview);
      }
    };
  }, [pendingAvatarPreview]);

  // Cleanup pending screen-reader announcement timer on unmount
  useEffect(() => {
    return () => {
      if (announceTimerRef.current) clearTimeout(announceTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setAvatarImageError(false);
  }, [avatarPreviewUrl]);

  // Auto-clear success message after 10 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 10000); // Clear after 10 seconds
      return () => clearTimeout(timer);
    }
  }, [successMessage]);


  // Remove the error for a single field once the user starts correcting it so
  // the summary and the field's invalid state update dynamically.
  const clearFieldError = (name) => {
    setFieldErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validateSingleField = (name, value) => {
    const meta = REQUIRED_PROFILE_FIELDS.find((field) => field.name === name);
    if (!meta) return;
    if (isEmptyValue(value)) {
      setFieldErrors(prev => ({ ...prev, [name]: `${meta.label} is required` }));
    } else {
      clearFieldError(name);
    }
  };

  const onFieldBlur = (e) => {
    const { name, value } = e.target;
    if (!REQUIRED_PROFILE_FIELDS.some((field) => field.name === name)) return;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateSingleField(name, value);
  };

  const onMultiSelectBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    // Defer so Syncfusion addTagOnBlur/change can commit the latest value first.
    setTimeout(() => {
      validateSingleField(name, formRef.current[name]);
    }, 0);
  };

  const notifyValidationChange = (opts) => {
    if (onValidationChange) {
      setTimeout(() => onValidationChange(opts), 0);
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (touched[name] || fieldErrors[name]) {
      validateSingleField(name, value);
    } else {
      clearFieldError(name);
    }
    notifyValidationChange();
  };

  // Validate all required profile fields. Returns an errors map keyed by field name.
  const validateForm = () => {
    const errors = {};
    REQUIRED_PROFILE_FIELDS.forEach(({ name, label }) => {
      if (isEmptyValue(form[name])) {
        errors[name] = `${label} is required`;
      }
    });
    return errors;
  };

  // Resolve the focusable control for a field. Native inputs/selects expose the
  // element directly; Syncfusion MultiSelect hides the original element behind a
  // visible combobox input, which is the element that actually receives focus.
  const getFocusTarget = (fieldId) => {
    const original = document.getElementById(fieldId);
    if (!original) return null;
    const wrapper = original.closest('.e-multiselect');
    if (wrapper) {
      return wrapper.querySelector('input.e-dropdownbase, input[role="combobox"]') || original;
    }
    return original;
  };

  const focusField = (fieldId) => {
    const target = getFocusTarget(fieldId);
    if (target && typeof target.focus === 'function') {
      target.focus();
      return true;
    }
    return false;
  };

  // Syncfusion comboboxes are outside React's control, so apply/remove their
  // error ARIA imperatively. This keeps aria-invalid and the aria-describedby
  // error reference in sync with fieldErrors for screen reader users.
  useEffect(() => {
    MULTISELECT_FIELDS.forEach((fieldId) => {
      const target = getFocusTarget(fieldId);
      if (!target) return;
      const errorId = `${fieldId}-error`;
      const summaryId = `${fieldId}-selection-summary`;
      const hasError = !!fieldErrors[fieldId];
      const ids = (target.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      const index = ids.indexOf(errorId);
      if (hasError && index === -1) {
        ids.push(errorId);
      } else if (!hasError && index !== -1) {
        ids.splice(index, 1);
      }
      // Keep the selection summary (managed by syncfusionMultiSelectA11y) so
      // current selections remain announced after error ARIA updates.
      if (document.getElementById(summaryId) && !ids.includes(summaryId)) {
        ids.unshift(summaryId);
      }
      if (ids.length) {
        target.setAttribute('aria-describedby', ids.join(' '));
      } else {
        target.removeAttribute('aria-describedby');
      }
      if (hasError) {
        target.setAttribute('aria-invalid', 'true');
      } else {
        target.removeAttribute('aria-invalid');
      }
    });
  }, [fieldErrors]);

  // languages handled by MultiSelect change below


  const presign = async (file, fileType) => {
    return authFetch('/api/uploads/presign', {
      method: 'POST',
      body: JSON.stringify({
        fileName: file.name,
        fileType,
        mimeType: file.type
      })
    });
  };

  const completeUpload = async (fileKey, fileType, file) => {
    return authFetch('/api/uploads/complete', {
      method: 'POST',
      body: JSON.stringify({
        fileKey,
        fileType,
        fileName: file.name,
        mimeType: file.type,
        size: file.size
      })
    });
  };

  const uploadToS3 = async (file, fileType) => {
    setUploading(true);
    try {
      if (fileType === 'resume') {
        setResumeStatusMessage(`Uploading resume file ${file.name}.`);
      }
      const presigned = await presign(file, fileType);
      const uploadUrl = presigned?.upload?.url;
      const key = presigned?.upload?.key;
      const immediateDownload = presigned?.download?.url;
      if (!uploadUrl || !key) throw new Error('Invalid presign response');

      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        mode: 'cors',
        headers: { 'Content-Type': file.type },
        body: file
      });
      if (!putRes.ok) throw new Error(`S3 upload failed: ${putRes.status}`);

      const complete = await completeUpload(key, fileType, file);

      if (fileType === 'resume') {
        setResumeFileName(file.name);
        setResumeKey(key);
        const completedUrl = complete?.file?.downloadUrl || immediateDownload;
        if (completedUrl) setResumeUrl(completedUrl);
        showToast('Resume uploaded', 'success');
        setResumeStatusMessage(`Resume uploaded successfully: ${file.name}.`);
        // Clear input so selecting the same file again triggers onChange
        if (resumeInputRef.current) resumeInputRef.current.value = '';
      } else if (fileType === 'avatar') {
        setAvatarKey(key);
        const displayUrl =
          complete?.file?.publicUrl || complete?.file?.downloadUrl || immediateDownload;
        if (!displayUrl) throw new Error('No URL returned after avatar upload');
        setAvatarPreviewUrl(displayUrl);
        showToast('Profile picture uploaded');
      }
    } catch (e) {
      // Surface likely CORS or network causes
      console.error('S3 upload error', e);
      const hint = e?.message?.includes('TypeError: Failed to fetch') ? ' (Check S3 CORS and origin http://localhost:3000)' : '';
      showToast((e.message || 'Upload failed') + hint, 'error');
      if (fileType === 'resume') {
        setResumeStatusMessage('Resume upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  // Camera controls
  const openCamera = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        showToast('Camera not supported', 'error');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 480 } },
        audio: false
      });
      mediaStreamRef.current = stream;
      returnFocusRef.current = document.activeElement || cameraTriggerRef.current;
      // Open modal first; video element will mount, then we bind stream in an effect
      setCameraOpen(true);
    } catch (e) {
      showToast('Unable to access camera', 'error');
    }
  };

  const closeCamera = useCallback(() => {
    try {
      const s = mediaStreamRef.current;
      if (s) s.getTracks().forEach(t => t.stop());
    } catch { }
    mediaStreamRef.current = null;
    setCameraOpen(false);
    const returnTarget = returnFocusRef.current || cameraTriggerRef.current;
    returnFocusRef.current = null;
    if (returnTarget && typeof returnTarget.focus === 'function') {
      setTimeout(() => {
        if (document.contains(returnTarget)) {
          returnTarget.focus();
        }
      }, 0);
    }
  }, []);

  const getCameraFocusableElements = () => {
    if (!cameraDialogRef.current) return [];
    return Array.from(
      cameraDialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
  };

  const handleCameraDialogKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeCamera();
      return;
    }
    if (e.key !== 'Tab') return;

    const focusableElements = getCameraFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (e.shiftKey) {
      if (activeElement === firstElement || !cameraDialogRef.current?.contains(activeElement)) {
        e.preventDefault();
        lastElement.focus();
      }
      return;
    }

    if (activeElement === lastElement || !cameraDialogRef.current?.contains(activeElement)) {
      e.preventDefault();
      firstElement.focus();
    }
  };

  useEffect(() => {
    if (!cameraOpen) return;
    const focusTimer = setTimeout(() => {
      const preferredFocusTarget = !cameraCaptureButtonRef.current?.disabled
        ? cameraCaptureButtonRef.current
        : cameraCancelButtonRef.current;
      const firstFocusable = getCameraFocusableElements()[0];
      (preferredFocusTarget || firstFocusable)?.focus();
    }, 0);
    return () => clearTimeout(focusTimer);
  }, [cameraOpen]);

  const capturePhoto = async () => {
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) return;
      const w = video.videoWidth || 480;
      const h = video.videoHeight || 480;
      const size = Math.min(w, h);
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      const sx = (w - size) / 2;
      const sy = (h - size) / 2;
      ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
        handleAvatarFileSelect(file, 'camera');
        closeCamera();
      }, 'image/jpeg', 0.9);
    } catch (e) {
      showToast('Failed to capture photo', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage(''); // Clear any previous success message

    // Validate required fields BEFORE saving. If anything is missing we must not
    // save, must not show a success message, and must move the screen reader's
    // focus to the first invalid field so the associated error is announced.
    const errors = validateForm();
    setFieldErrors(errors);
    setSubmitAttempted(true);
    setTouched(prev => {
      const next = { ...prev };
      REQUIRED_PROFILE_FIELDS.forEach(({ name }) => { next[name] = true; });
      return next;
    });
    const firstInvalid = REQUIRED_PROFILE_FIELDS.find(({ name }) => errors[name]);
    if (firstInvalid) {
      notifyValidationChange({ attempted: true });
      // Defer so the error summary and inline messages render (and the imperative
      // ARIA sync runs) before we move focus to the first invalid field.
      setTimeout(() => {
        focusField(firstInvalid.field);
      }, 60);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        linkedInUrl: (form.linkedInUrl || '').trim() || null,
        profile: {
          headline: form.headline,
          keywords: form.keywords,
          primaryExperience: form.primaryExperience,
          workLevel: form.workLevel,
          educationLevel: form.educationLevel,
          languages: form.languages,
          employmentTypes: form.employmentTypes,
          veteranStatus: form.veteranStatus
        }
      };
      await authFetch('/api/users/me', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      // In the wizard, onDone runs the parent's step validation (e.g. resume
      // requirement) and returns false when it blocks advancing. Only confirm
      // success when we are not being blocked, so we never show a success
      // message alongside outstanding errors.
      let proceeded = true;
      if (onDone) {
        const result = onDone();
        proceeded = result !== false;
      }

      if (proceeded) {
        setSuccessMessage('Profile updated successfully!');

        // Scroll to top smoothly, then move focus to the confirmation.
        setTimeout(() => {
          if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
          setTimeout(() => {
            if (successMessageRef.current) {
              successMessageRef.current.focus();
            }
          }, 300);
        }, 100);
      }
    } catch (e) {
      showToast('Failed to save', 'error');
      setSuccessMessage(''); // Clear success message on error
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {!embedded && (
        <Helmet>
          <title>Edit Profile & Resume - abilityconnect</title>
        </Helmet>
      )}
      <div className="dashboard-content">
        <div ref={topRef} style={{ position: 'absolute', top: 0 }} aria-hidden="true" />
        {/* Persistent live regions: always present in the DOM so screen readers
            (VoiceOver) reliably announce resume/photo upload, add, and delete
            results. Text is set imperatively via refs in announceToScreenReader. */}
        <div
          ref={politeRegionRef}
          aria-live="polite"
          aria-atomic="true"
          style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}
        />
        <div
          ref={assertiveRegionRef}
          aria-live="assertive"
          aria-atomic="true"
          style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}
        />
        {toast.visible && (
          <div className={`toast ${toast.type}`} aria-hidden="true">{toast.message}</div>
        )}
      {/* Persistent alert region for form-level status and validation errors.
          It is always present in the DOM so the screen reader announces whatever
          content is injected after submit — either the list of field errors or
          the success confirmation. The inner elements intentionally do NOT carry
          their own role/aria-live; the parent owns the live region. */}
      <div role="alert" aria-live="assertive" aria-atomic="true">
        {/* In the registration wizard the parent already renders a step-level
            error summary — skip this duplicate when embedded. */}
        {!embedded && submitAttempted && hasFieldErrors && (
          <div
            ref={errorSummaryRef}
            className="form-error-summary"
            tabIndex={-1}
          >
            <strong>Please complete all required fields:</strong>
            <ul>
              {REQUIRED_PROFILE_FIELDS.filter(({ name }) => fieldErrors[name]).map(({ name, field }) => (
                <li key={name}>
                  <a
                    href={`#${field}`}
                    className="form-error-summary-link"
                    onClick={(e) => {
                      e.preventDefault();
                      focusField(field);
                    }}
                  >
                    {fieldErrors[name]}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        {successMessage && (
          <div
            ref={successMessageRef}
            className="success-message"
            tabIndex={-1}
            style={{
              padding: '1rem',
              marginBottom: '1.5rem',
              backgroundColor: '#d1fae5',
              border: '2px solid #10b981',
              borderRadius: '8px',
              color: '#065f46',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <span aria-hidden="true" style={{ fontSize: '1.25rem' }}>✓</span>
            <span>{successMessage}</span>
          </div>
        )}
      </div>
      {!embedded && <h1>My Profile</h1>}
      {!embedded && <p className="section-note">Edit the form below to update your profile information.</p>}

        {infoBannerMessage && (
          <div className="info-banner">
            <span>{infoBannerMessage}</span>
          </div>
        )}

      <div className="upload-row">
        <div className="upload-card">
          <ProfileSectionHeading id="resume-section-label">
            Resume{!resumeOptional ? ' *' : ''}
          </ProfileSectionHeading>
          {resumeTopSlot}
          {/* Tab toggle — only in standalone (not embedded in wizard which has its own source selector) */}
          {!resumeOptional && !resumeTopSlot && (
            <div className="resume-method-selector">
              <p id="resume-method-instructions" className="muted resume-method-instructions">
                Choose the method you would like to add your resume.
              </p>
              <div
                className="resume-method-toggle"
                role="radiogroup"
                aria-labelledby="resume-section-label"
                aria-describedby="resume-method-instructions"
              >
                <label className={`resume-method-option ${resumeTab === 'upload' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="resume-method"
                    value="upload"
                    checked={resumeTab === 'upload'}
                    onChange={() => { setResumeTab('upload'); localStorage.setItem('resumeTabPref', 'upload'); }}
                    aria-label="Upload Resume from File"
                    aria-describedby="resume-method-upload-desc"
                  />
                  <span>Upload Resume from File</span>
                  <span id="resume-method-upload-desc" className="sr-only">
                    Upload a PDF, DOC, or DOCX resume file from your device.
                  </span>
                </label>
                <label className={`resume-method-option ${resumeTab === 'builder' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="resume-method"
                    value="builder"
                    checked={resumeTab === 'builder'}
                    onChange={() => { setResumeTab('builder'); localStorage.setItem('resumeTabPref', 'builder'); }}
                    aria-label="Add from Resume Builder"
                    aria-describedby="resume-method-builder-desc"
                  />
                  <span>Add from Resume Builder</span>
                  <span id="resume-method-builder-desc" className="sr-only">
                    Use a resume created in Resume Builder.
                  </span>
                </label>
              </div>
            </div>
          )}
          {/* Upload UI — shown when upload tab is active, or when embedded in wizard with resumeSource='upload' (resumeOptional=false) */}
          {!resumeOptional && (resumeTopSlot || resumeTab === 'upload') && (
            <>
              <p id="resume-file-types" className="muted resume-file-requirements">Accepted file types: PDF and DOC (PDF preferred)</p>
              <div
                id="resume-file-status"
                style={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  padding: 0,
                  margin: -1,
                  overflow: 'hidden',
                  clip: 'rect(0, 0, 0, 0)',
                  whiteSpace: 'nowrap',
                  border: 0
                }}
              >
                {resumeStatusMessage}
              </div>
              {resumeFileName && (
                <div className="resume-file-status-card">
                  <div className="resume-file-info">
                    <span id="resume-file-label">File Attached:</span>
                    <span className="resume-file-name"><strong>{resumeFileName}</strong></span>
                  </div>
                  <div className="resume-file-actions">
                    {resumeUrl && (
                      <a
                        href={resumeUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="update-button resume-file-action-btn"
                        style={{ padding: '0.25rem 0.5rem', marginBottom: 0 }}
                        aria-label={`View ${resumeFileName}`}
                      >
                        View
                      </a>
                    )}
                    <button
                      type="button"
                      className="update-button resume-file-action-btn"
                      style={{ marginBottom: 0 }}
                      onClick={deleteResume}
                      disabled={!resumeKey}
                      aria-label={`Delete ${resumeFileName}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
              <div className="upload-actions resume-upload-control">
                <input
                  ref={resumeInputRef}
                  id="resume-file-input"
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadToS3(f, 'resume');
                  }}
                />
                <button
                  type="button"
                  className="update-button"
                  onClick={() => resumeInputRef.current?.click()}
                  disabled={uploading}
                  aria-label={uploading ? 'Uploading resume file' : 'Choose file to upload'}
                  aria-describedby={`resume-file-types resume-file-status ${resumeError ? 'resume-upload-error' : ''}`.trim()}
                  aria-required={!resumeOptional ? 'true' : undefined}
                >
                  {uploading ? 'Uploading…' : 'Choose file to upload'}
                </button>
              </div>
              {resumeError && (
                <div id="resume-upload-error" className="field-error" role="alert" aria-live="polite">
                  {resumeError}
                </div>
              )}
            </>
          )}
          {/* Builder list — only in standalone mode when builder tab is active */}
          {!resumeOptional && !resumeTopSlot && resumeTab === 'builder' && (
            <div>
              <p className="muted" style={{ marginBottom: '0.75rem' }}>Select a resume from your Resume Builder to use as your profile resume.</p>
              {builderLoading ? (
                <p className="muted">Loading resumes…</p>
              ) : builderResumes.length === 0 ? (
                <p className="muted">No resumes found. Create one in the <strong>Resume Builder</strong> section.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {builderResumes.map(r => (
                    <div key={r._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', border: `1px solid ${r.isDefault ? '#1f2937' : '#e5e7eb'}`, borderRadius: '6px', background: r.isDefault ? '#f3f4f6' : '#fff' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '14px', fontWeight: r.isDefault ? 600 : 400, color: '#111827' }}>
                          {r.title || 'Untitled Resume'}
                        </span>
                        {r.isDefault && (
                          <span style={{ fontSize: '11px', background: '#1f2937', color: '#fff', borderRadius: '999px', padding: '1px 8px' }}>Profile Resume</span>
                        )}
                      </div>
                      {!r.isDefault && (
                        <button
                          type="button"
                          className="update-button"
                          style={{ padding: '4px 10px', fontSize: '12px' }}
                          disabled={settingDefault === r._id}
                          onClick={() => handleSetDefault(r._id)}
                        >
                          {settingDefault === r._id ? 'Setting…' : 'Use as Profile Resume'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="upload-card">
          <ProfileSectionHeading>Add a Profile Picture (Optional)</ProfileSectionHeading>
          <p className="muted">Accepted file types: JPG, PNG, GIF (max size: 2MB)</p>
          <p id="avatar-instructions" className="muted avatar-instructions">
            {avatarInstructions}
          </p>
          
          {/* Current Avatar Preview (already uploaded) */}
          {avatarPreviewUrl && !pendingAvatarPreview && (
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
              <div
                className="avatar-preview avatar-preview-saved"
                aria-label="Profile picture preview"
              >
                {!avatarImageError ? (
                  <img
                    src={avatarPreviewUrl}
                    alt=""
                    className="avatar-preview-saved-img"
                    onError={() => setAvatarImageError(true)}
                  />
                ) : (
                  <span className="avatar-preview-saved-initials" aria-hidden="true">
                    {getNameInitials(user?.name)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Pending Avatar Preview (before upload) */}
          {pendingAvatarPreview && (
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
              <div 
                className="avatar-preview" 
                style={{ 
                  width: '150px', 
                  height: '150px', 
                  borderRadius: '50%', 
                  border: '2px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
                aria-label="New profile picture preview"
              >
                <img 
                  src={pendingAvatarPreview} 
                  alt="Selected profile preview" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover', 
                    display: 'block'
                  }}
                  onError={(e) => {
                    console.error('Preview image failed to load');
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="avatar-pending-meta">
                {pendingAvatarSource === 'camera' ? (
                  <span className="avatar-pending-name"><strong>Photo Taken</strong></span>
                ) : (
                  <>
                    <span>File name:</span>
                    <span className="avatar-pending-name"><strong>{pendingAvatarName || 'Selected image'}</strong></span>
                  </>
                )}
              </div>
              <div className="avatar-pending-actions">
                <button 
                  type="button" 
                  className="update-button avatar-action-btn avatar-action-btn-primary" 
                  onClick={confirmAvatarUpload}
                  disabled={uploading}
                  aria-describedby="avatar-instructions"
                >
                  {uploading ? 'Saving…' : 'Confirm'}
                </button>
                <button 
                  type="button" 
                  className="dashboard-button avatar-action-btn avatar-action-btn-secondary" 
                  onClick={cancelAvatarUpload}
                  disabled={uploading}
                  aria-describedby="avatar-instructions"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Upload Controls */}
          {!pendingAvatarPreview && (
            <div className="avatar-upload-controls">
              <div className="avatar-file-actions">
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleAvatarFileSelect(f);
                  }}
                />
                <button
                  type="button"
                  className="update-button avatar-file-btn"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploading}
                  aria-describedby="avatar-instructions"
                >
                  Choose Picture
                </button>
                {avatarPreviewUrl && (
                  <button 
                    type="button" 
                    className="update-button avatar-file-btn" 
                    onClick={deleteAvatar} 
                    aria-describedby="avatar-instructions"
                  >
                    Remove Picture
                  </button>
                )}
              </div>
              <button
                type="button"
                className="dashboard-button avatar-camera-btn"
                onClick={openCamera}
                disabled={uploading}
                ref={cameraTriggerRef}
                aria-describedby="avatar-instructions"
              >
                Take your Picture
              </button>
            </div>
          )}
        </div>
      </div>

      <form aria-label="Profile and resume form" className="account-form" onSubmit={handleSubmit}>
        <p className="section-note">An asterisk (*) indicates a required field.</p>

        <div className="form-group">
          <div className="label-with-counter">
            <label htmlFor="headline">* Your Headline</label>
            <span
              id="headline-counter"
              className={`char-counter ${
                form.headline.length > 150 ? 'char-counter-over' :
                form.headline.length >= 145 ? 'char-counter-danger' :
                form.headline.length >= 140 ? 'char-counter-warning' :
                form.headline.length > 130 ? 'char-counter-caution' : ''
              }`}
            >
              {form.headline.length}/150 characters
            </span>
          </div>
          <input 
            id="headline" 
            name="headline" 
            value={form.headline} 
            onChange={onChange}
            onBlur={onFieldBlur}
            placeholder="Make a brief statement about yourself" 
            maxLength={150}
            aria-describedby={`headline-hint headline-counter${fieldErrors.headline ? ' headline-error' : ''}`}
            aria-invalid={fieldErrors.headline ? 'true' : undefined}
          />
          <span id="headline-hint" className="visually-hidden">
            Maximum 150 characters.
          </span>
          {fieldErrors.headline && (
            <span id="headline-error" className="field-error" role="alert">{fieldErrors.headline}</span>
          )}
        </div>

        <div className="form-group">
          <label id="keyword-input-label">* Keywords (Skills and Experience)</label>
          <div id="keyword-input-instructions" className="field-help">
            To complete this box, type keywords and press Enter after each keyword entry. To delete an entry, you will need to press Backspace.
          </div>
          <MultiSelectComponent
            id="keywords"
            aria-labelledby="keyword-input-label"
            aria-describedby="keyword-input-instructions"
            dataSource={keywordTags}
            value={keywordTags}
            mode="Box"
            placeholder="Your skills, job titles, certifications, etc."
            cssClass="ajf-input"
            showDropDownIcon={true}
            popupHeight="220px"
            allowFiltering={true}
            allowCustomValue={true}
            addTagOnBlur={true}
            {...getSyncfusionMultiSelectA11yHandlers({
              inputId: 'keywords',
              labelId: 'keyword-input-label',
              instructionsId: 'keyword-input-instructions',
              listboxLabel: 'Keywords options',
              fieldLabel: 'Keywords',
              onChange: (args) => {
                const values = Array.isArray(args?.value)
                  ? args.value.map((entry) => String(entry || '').trim()).filter(Boolean)
                  : [];
                const uniqueKeywords = [...new Set(values)];
                setForm(prev => ({ ...prev, keywords: uniqueKeywords.join(', ') }));
                if (touched.keywords || fieldErrors.keywords) {
                  validateSingleField('keywords', uniqueKeywords.join(', '));
                } else {
                  clearFieldError('keywords');
                }
                notifyValidationChange();
              },
            })}
            blur={() => onMultiSelectBlur('keywords')}
          />
          {fieldErrors.keywords && (
            <span id="keywords-error" className="field-error" role="alert">{fieldErrors.keywords}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="linkedInUrl">LinkedIn Profile URL (Optional)</label>
          <input
            id="linkedInUrl"
            name="linkedInUrl"
            type="url"
            value={form.linkedInUrl}
            onChange={onChange}
            placeholder="https://www.linkedin.com/in/your-profile"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label id="primaryExperience-label">* Primary Job Experience (maximum 2)</label>
            <span id="primaryExperience-instructions" className="sr-only">
              Select one or two Primary Job Experience. Use the left and right arrow to navigate selections.
            </span>
            <MultiSelectComponent
              id="primaryExperience"
              aria-labelledby="primaryExperience-label"
              aria-describedby="primaryExperience-instructions"
              dataSource={JOB_CATEGORY_LIST}
              fields={{ text: 'name', value: 'value' }}
              value={form.primaryExperience}
              mode="Box"
              placeholder="Select up to 2"
              maximumSelectionLength={2}
              cssClass="ajf-input"
              showDropDownIcon={true}
              popupHeight="260px"
              {...getSyncfusionMultiSelectA11yHandlers({
                inputId: 'primaryExperience',
                labelId: 'primaryExperience-label',
                instructionsId: 'primaryExperience-instructions',
                listboxLabel: 'Primary Job Experience options',
                fieldLabel: 'Primary Job Experience',
                onChange: (args) => {
                  const values = Array.isArray(args?.value) ? args.value : [];
                  const nextValues = values.slice(0, 2);
                  setForm(prev => ({ ...prev, primaryExperience: nextValues }));
                  if (touched.primaryExperience || fieldErrors.primaryExperience) {
                    validateSingleField('primaryExperience', nextValues);
                  } else {
                    clearFieldError('primaryExperience');
                  }
                  notifyValidationChange();
                },
              })}
              blur={() => onMultiSelectBlur('primaryExperience')}
            />
            {fieldErrors.primaryExperience && (
              <span id="primaryExperience-error" className="field-error" role="alert">{fieldErrors.primaryExperience}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="workLevel">* Work Experience Level</label>
            <select
              id="workLevel"
              name="workLevel"
              value={form.workLevel}
              onChange={onChange}
              onBlur={onFieldBlur}
              aria-describedby={fieldErrors.workLevel ? 'workLevel-error' : undefined}
              aria-invalid={fieldErrors.workLevel ? 'true' : undefined}
            >
              <option value="">Select level</option>
              {EXPERIENCE_LEVEL_LIST.map(o => (
                <option key={o.value} value={o.value}>{o.name}</option>
              ))}
            </select>
            {fieldErrors.workLevel && (
              <span id="workLevel-error" className="field-error" role="alert">{fieldErrors.workLevel}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="educationLevel">* Highest Education Level</label>
            <select
              id="educationLevel"
              name="educationLevel"
              value={form.educationLevel}
              onChange={onChange}
              onBlur={onFieldBlur}
              aria-describedby={fieldErrors.educationLevel ? 'educationLevel-error' : undefined}
              aria-invalid={fieldErrors.educationLevel ? 'true' : undefined}
            >
              <option value="">Select education</option>
              {EDUCATION_LEVEL_LIST.map(o => (
                <option key={o.value} value={o.value}>{o.name}</option>
              ))}
            </select>
            {fieldErrors.educationLevel && (
              <span id="educationLevel-error" className="field-error" role="alert">{fieldErrors.educationLevel}</span>
            )}
          </div>
          <div className="form-group">
            <label id="employmentTypes-label">* Employment Types</label>
            <span id="employmentTypes-instructions" className="sr-only">
              Select one or more Employment Types. Use the left and right arrow to navigate selections.
            </span>
            <MultiSelectComponent
              id="employmentTypes"
              aria-labelledby="employmentTypes-label"
              aria-describedby="employmentTypes-instructions"
              dataSource={JOB_TYPE_LIST}
              fields={{ text: 'name', value: 'value' }}
              value={form.employmentTypes}
              mode="Box"
              placeholder="Select employment types"
              cssClass="ajf-input"
              showDropDownIcon={true}
              popupHeight="260px"
              {...getSyncfusionMultiSelectA11yHandlers({
                inputId: 'employmentTypes',
                labelId: 'employmentTypes-label',
                instructionsId: 'employmentTypes-instructions',
                listboxLabel: 'Employment Types options',
                fieldLabel: 'Employment Types',
                onChange: (args) => {
                  const values = Array.isArray(args?.value) ? args.value : [];
                  setForm(prev => ({ ...prev, employmentTypes: values }));
                  if (touched.employmentTypes || fieldErrors.employmentTypes) {
                    validateSingleField('employmentTypes', values);
                  } else {
                    clearFieldError('employmentTypes');
                  }
                  notifyValidationChange();
                },
              })}
              blur={() => onMultiSelectBlur('employmentTypes')}
            />
            {fieldErrors.employmentTypes && (
              <span id="employmentTypes-error" className="field-error" role="alert">{fieldErrors.employmentTypes}</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label id="languages-label">* Language(s)</label>
          <span id="languages-instructions" className="sr-only">
            Select one or more Language(s). Use the left and right arrow to navigate selections.
          </span>
          <MultiSelectComponent
            id="languages"
            aria-labelledby="languages-label"
            aria-describedby="languages-instructions"
            dataSource={LANGUAGE_LIST}
            fields={{ text: 'name', value: 'value' }}
            value={form.languages}
            mode="Box"
            placeholder="Select languages"
            cssClass="ajf-input"
            showDropDownIcon={true}
            popupHeight="260px"
            allowFiltering={true}
            {...getSyncfusionMultiSelectA11yHandlers({
              inputId: 'languages',
              labelId: 'languages-label',
              instructionsId: 'languages-instructions',
              listboxLabel: 'Language options',
              fieldLabel: 'Language(s)',
              onChange: (args) => {
                const values = Array.isArray(args?.value) ? args.value : [];
                setForm(prev => ({ ...prev, languages: values }));
                if (touched.languages || fieldErrors.languages) {
                  validateSingleField('languages', values);
                } else {
                  clearFieldError('languages');
                }
                notifyValidationChange();
              },
            })}
            blur={() => onMultiSelectBlur('languages')}
          />
          {fieldErrors.languages && (
            <span id="languages-error" className="field-error" role="alert">{fieldErrors.languages}</span>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="veteranStatus">Veteran/Military Status</label>
            <select id="veteranStatus" name="veteranStatus" value={form.veteranStatus} onChange={onChange}>
              <option value="">Select</option>
              {MILITARY_EXPERIENCE_LIST.map(o => (
                <option key={o.value} value={o.value}>{o.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-actions" style={{ display: 'flex', justifyContent: onPrev ? 'space-between' : 'flex-start', gap: '1rem', marginTop: '1.5rem', marginBottom: '3rem' }}>
          {onPrev && (
            <button type="button" className="ajf-btn ajf-btn-outline" onClick={onPrev} aria-label="Go to previous step">
              Previous
            </button>
          )}
          <button type="submit" className={onDone ? "ajf-btn ajf-btn-dark" : "update-button"} disabled={saving || (embedded && !profileReady)}>
            {saving ? 'Saving…' : (onDone ? 'Save and Next' : 'Submit')}
          </button>
        </div>
      </form>

      {cameraOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div
            ref={cameraDialogRef}
            className="camera-dialog-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="take-picture-dialog-title"
            onKeyDown={handleCameraDialogKeyDown}
            style={{ background: '#fff', borderRadius: 8, padding: 16, width: 520, maxWidth: '95vw', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}
          >
            <CameraDialogHeading id="take-picture-dialog-title" className="camera-dialog-title" style={{ marginTop: 0 }}>Take your picture</CameraDialogHeading>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, placeItems: 'center' }}>
              <video ref={videoRef} playsInline muted style={{ width: 480, height: 360, background: '#000', maxWidth: '100%' }} />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
            <div className="camera-modal-actions">
              <button
                ref={cameraCaptureButtonRef}
                type="button"
                className="dashboard-button camera-modal-btn camera-modal-btn-primary"
                onClick={capturePhoto}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Capture & Use Photo'}
              </button>
              <button
                ref={cameraCancelButtonRef}
                type="button"
                className="dashboard-button camera-modal-btn camera-modal-btn-secondary"
                onClick={closeCamera}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}
